// KYC pendientes (F2): lista de clientes en revisión manual + acción Aprobar / Rechazar. Conectado
// al backend real (retail-customer-business): listKycPending + reviewCustomerKyc. Rol admin_surekeep.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Badge, Button, EmptyState, ErrorState, Loading, Modal } from '@/kit';
import {
    customerFullName,
    listKycPending,
    reviewCustomerKyc,
    type KycReviewResult,
    type RetailCustomerSummary,
} from '@/api/services/retail-customer';
import { kycPendingErrorCopy } from './kyc/lib/kycErrors';

function formatPhone(phone: string): string {
    const d = phone.replace(/\D/g, '');
    return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : phone;
}

export function SalesKycPending() {
    const qc = useQueryClient();
    const [target, setTarget] = useState<{
        c: RetailCustomerSummary;
        result: KycReviewResult;
    } | null>(null);
    const [justification, setJustification] = useState('');

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['retail-customer', 'kyc-pending'],
        queryFn: listKycPending,
    });
    const pending = data ?? [];
    const errorCopy = error ? kycPendingErrorCopy(error) : null;

    // El cliente a revisar viaja como VARIABLE de la mutación, no leído del estado con `!`: además
    // de sacarse las tres aserciones non-null, evita que `onSuccess` cante el toast equivocado si
    // el estado cambió mientras la petición volaba.
    const review = useMutation({
        mutationFn: (t: { c: RetailCustomerSummary; result: KycReviewResult }) =>
            reviewCustomerKyc(t.c.retailCustomerId, t.result, justification.trim()),
        onSuccess: (_data, t) => {
            toast.success(t.result === 'APPROVED' ? 'KYC aprobado' : 'KYC rechazado');
            void qc.invalidateQueries({ queryKey: ['retail-customer'] });
            setTarget(null);
            setJustification('');
        },
        onError: () => toast.error('No se pudo registrar la revisión'),
    });

    const open = (c: RetailCustomerSummary, result: KycReviewResult) => {
        setJustification('');
        setTarget({ c, result });
    };

    return (
        <div className="flex flex-col gap-4">
            {isLoading ? (
                <Loading size="lg" />
            ) : errorCopy ? (
                <ErrorState
                    title={errorCopy.title}
                    description={errorCopy.description}
                    onRetry={
                        errorCopy.canRetry
                            ? () => {
                                  void refetch();
                              }
                            : undefined
                    }
                />
            ) : pending.length === 0 ? (
                <EmptyState
                    title="Sin KYC pendientes"
                    description="Cuando un cliente quede en revisión manual, aparecerá aquí para aprobar o rechazar."
                    icon={<ShieldCheck className="size-6" aria-hidden />}
                />
            ) : (
                <div className="flex flex-col gap-2">
                    {pending.map((c) => (
                        <div
                            key={c.retailCustomerId}
                            className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5"
                        >
                            <div className="min-w-0">
                                <p className="truncate text-[13px] font-medium">
                                    {customerFullName(c)}
                                </p>
                                <p className="truncate text-[11.5px] text-muted-foreground">
                                    {formatPhone(c.phone)} · {c.curp ?? '—'}
                                </p>
                            </div>
                            <div className="flex flex-none items-center gap-2">
                                <Badge variant="secondary">Revisión manual</Badge>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        open(c, 'REJECTED');
                                    }}
                                >
                                    Rechazar
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        open(c, 'APPROVED');
                                    }}
                                >
                                    Aprobar
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                isOpen={!!target}
                onClose={() => {
                    setTarget(null);
                }}
                title={target?.result === 'APPROVED' ? 'Aprobar KYC' : 'Rechazar KYC'}
                size="sm"
            >
                {target ? (
                    <div className="flex flex-col gap-3">
                        <p className="text-[13px]">
                            {target.result === 'APPROVED' ? 'Aprobar' : 'Rechazar'} el KYC de{' '}
                            <span className="font-medium">{customerFullName(target.c)}</span>.
                        </p>
                        <label className="block space-y-1">
                            <span className="text-[11px] font-medium text-muted-foreground">
                                Justificación
                            </span>
                            <textarea
                                value={justification}
                                onChange={(e) => {
                                    setJustification(e.target.value);
                                }}
                                rows={3}
                                placeholder="Motivo de la decisión…"
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-foreground"
                            />
                        </label>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setTarget(null);
                                }}
                                disabled={review.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => {
                                    review.mutate(target);
                                }}
                                disabled={review.isPending || justification.trim().length === 0}
                            >
                                {review.isPending
                                    ? 'Guardando…'
                                    : target.result === 'APPROVED'
                                      ? 'Aprobar'
                                      : 'Rechazar'}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
}
